import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Globe, Plus, X, Zap, Lock, Crown, Archive, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AppLayout } from "@/components/AppLayout";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { value: 'US', label: 'United States' }, { value: 'UK', label: 'United Kingdom' },
  { value: 'EU', label: 'European Union' }, { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' }, { value: 'PL', label: 'Poland' },
  { value: 'ES', label: 'Spain' }, { value: 'IT', label: 'Italy' }
];

const LANGUAGES = [
  { value: 'en', label: 'English' }, { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' }, { value: 'pl', label: 'Polish' },
  { value: 'es', label: 'Spanish' }, { value: 'it', label: 'Italian' }
];

const DEPTHS = [
  { value: 10, label: 'Free', desc: '10 pages', tier: 'free' },
  { value: 0, label: 'Core/Pro', desc: 'Full Depth', tier: 'core' }
];

export default function FullAuditSetup() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [targetUrl, setTargetUrl] = useState("");
  const [competitors, setCompetitors] = useState([]);
  const [region, setRegion] = useState(""); // Empty by default - required for first audit
  const [language, setLanguage] = useState(""); // Empty by default - required for first audit
  const [depth, setDepth] = useState(0); // Default to Full Depth
  const [loading, setLoading] = useState(false);
  const [restrictPath, setRestrictPath] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [largeSiteWarning, setLargeSiteWarning] = useState(null); // {url_count: number}
  
  // Domain management state
  const [domains, setDomains] = useState([]);
  const [selectedDomainId, setSelectedDomainId] = useState("");
  const [canAddDomain, setCanAddDomain] = useState(false);
  const [maxDomains, setMaxDomains] = useState(0);
  const [showAddDomainInput, setShowAddDomainInput] = useState(false);
  const [newDomainInput, setNewDomainInput] = useState("");
  const [addingDomain, setAddingDomain] = useState(false);
  
  // Domain autocomplete state
  const [domainSuggestions, setDomainSuggestions] = useState([]);
  const [showDomainSuggestions, setShowDomainSuggestions] = useState(false);

  useEffect(() => {
    if (user) {
      fetchSubscription();
      fetchDomains();
      fetchUserPreferences();
      fetchDomainHistory();
    }
  }, [user]);

  // Auto-set depth based on user tier
  useEffect(() => {
    // Both Core and Pro get Full Depth
    if (isAdmin || subscription?.tier === 'pro' || subscription?.tier === 'core' || subscription?.tier === 'enterprise') {
      setDepth(0); // Full Depth
    }
  }, [subscription, isAdmin]);

  // Fetch user's saved preferences (region/language)
  const fetchUserPreferences = async () => {
    try {
      const res = await axios.get(`${API}/user/preferences`);
      if (res.data.default_region) setRegion(res.data.default_region);
      if (res.data.default_language) setLanguage(res.data.default_language);
    } catch (err) {
      // No preferences saved yet
    }
  };

  // Fetch domain history for autocomplete
  const fetchDomainHistory = async () => {
    try {
      const res = await axios.get(`${API}/user/domain-history`);
      setDomainSuggestions(res.data.domains || []);
    } catch (err) {
      // No history
    }
  };

  const fetchSubscription = async () => {
    try {
      const res = await axios.get(`${API}/subscription`);
      setSubscription(res.data);
    } catch (err) {
      // User not logged in or error
    }
  };

  const fetchDomains = async () => {
    try {
      const res = await axios.get(`${API}/domains`);
      setDomains(res.data.domains || []);
      setCanAddDomain(res.data.can_add_domain);
      setMaxDomains(res.data.max_domains);
      // Auto-select primary domain if available
      if (res.data.primary_domain_id && res.data.domains?.length > 0) {
        const primary = res.data.domains.find(d => d.id === res.data.primary_domain_id);
        if (primary) {
          setSelectedDomainId(primary.id);
          setTargetUrl(primary.url);
        }
      } else if (res.data.domains?.length > 0) {
        // Select first domain if no primary
        setSelectedDomainId(res.data.domains[0].id);
        setTargetUrl(res.data.domains[0].url);
      }
    } catch (err) {
      // User not logged in or error
    }
  };

  const handleAddDomain = async () => {
    if (!newDomainInput.trim()) {
      toast.error("Enter a domain");
      return;
    }
    setAddingDomain(true);
    try {
      const res = await axios.post(`${API}/domains`, { domain: newDomainInput.trim() });
      toast.success(`Domain "${res.data.domain.domain}" added`);
      setNewDomainInput("");
      setShowAddDomainInput(false);
      await fetchDomains();
      // Select the newly added domain
      setSelectedDomainId(res.data.domain.id);
      setTargetUrl(res.data.domain.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add domain");
    } finally {
      setAddingDomain(false);
    }
  };

  const handleChangeDomain = async () => {
    if (!newDomainInput.trim()) {
      toast.error("Enter a domain");
      return;
    }
    setAddingDomain(true);
    try {
      // Archive old domain first
      if (domains.length > 0 && domains[0].id) {
        await axios.delete(`${API}/domains/${domains[0].id}`);
      }
      // Add new domain
      const res = await axios.post(`${API}/domains`, { domain: newDomainInput.trim() });
      toast.success(`Domain changed to "${res.data.domain.domain}"`);
      setNewDomainInput("");
      setShowAddDomainInput(false);
      await fetchDomains();
      setSelectedDomainId(res.data.domain.id);
      setTargetUrl(res.data.domain.url);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change domain");
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDomainSelect = (domainId) => {
    if (domainId === "__add_new__") {
      setShowAddDomainInput(true);
      return;
    }
    const domain = domains.find(d => d.id === domainId);
    if (domain) {
      setSelectedDomainId(domainId);
      setTargetUrl(domain.url);
    }
  };

  // Determine user tier and capabilities
  const userTier = isAdmin ? 'admin' : (subscription?.tier || 'free');
  const canAddCompetitors = isAdmin || userTier === 'pro';
  const hasCredits = isAdmin || (subscription?.credits_remaining > 0);
  // Free users CAN run audits if they have credits (1 free audit)
  const canRunFullAudit = isAdmin || hasCredits;
  const freeAuditUsed = userTier === 'free' && !hasCredits;
  const showUpgradeCta = user && !isAdmin && !canRunFullAudit;

  const addCompetitor = () => {
    if (canAddCompetitors && competitors.length < 3) {
      setCompetitors([...competitors, ""]);
    }
  };
  
  const removeCompetitor = (i) => setCompetitors(competitors.filter((_, idx) => idx !== i));
  
  const updateCompetitor = (i, val) => {
    const u = [...competitors];
    u[i] = val;
    setCompetitors(u);
  };

  // Filter domain suggestions based on input
  const filteredSuggestions = targetUrl.trim() 
    ? domainSuggestions.filter(d => 
        d.toLowerCase().includes(targetUrl.toLowerCase()) && 
        d.toLowerCase() !== targetUrl.toLowerCase()
      ).slice(0, 5)
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!targetUrl.trim()) { toast.error("Enter a target domain"); return; }
    
    // Validate region and language are selected
    if (!region) { toast.error("Please select a target region"); return; }
    if (!language) { toast.error("Please select a report language"); return; }
    
    if (!user) {
      toast.error("You must be logged in to run a full audit");
      navigate('/login');
      return;
    }

    if (!canRunFullAudit) {
      toast.error("You need an active subscription with credits to run a full audit");
      navigate('/pricing');
      return;
    }

    // Core users cannot have competitors
    if (!canAddCompetitors && competitors.filter(c => c.trim()).length > 0) {
      toast.error("Upgrade to Pro to add competitors");
      return;
    }

    // Pre-flight check: warn about large sites
    setLoading(true);
    try {
      const checkRes = await axios.post(`${API}/check-sitemap`, { url: targetUrl.trim() });
      const urlCount = checkRes.data?.url_count || 0;
      if (urlCount > 500) {
        setLargeSiteWarning({ url_count: urlCount });
        setLoading(false);
        return; // Wait for user confirmation
      }
    } catch (e) {
      // Sitemap check failed - proceed anyway
    }

    await runAudit();
  };

  const runAudit = async () => {
    setLoading(true);
    try {
      // Save preferences for future audits
      await axios.post(`${API}/user/preferences`, {
        default_region: region,
        default_language: language
      }).catch(() => {}); // Ignore errors
      
      const res = await axios.post(`${API}/audit`, {
        target_url: targetUrl.trim(),
        competitors: canAddCompetitors ? competitors.filter(c => c.trim()) : [],
        region, language, audit_depth: depth,
        report_mode: "paid_full",
        restrict_to_path: restrictPath
      });
      navigate(`/audit/${res.data.audit_id}`);
    } catch (err) {
      toast.error("Failed: " + (err.response?.data?.detail || err.message));
      setLoading(false);
    }
  };

  return (
    <AppLayout breadcrumbs={[
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'New Audit' }
    ]}>
      <Helmet>
        <title>Run Audit | ScoreCite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <div className="flex items-center justify-center p-4 py-8" data-testid="full-audit-page">
        <div className="w-full max-w-2xl">
          <Card className="bg-[#121212] border-white/10">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-heading text-xl text-white">Full Audit</CardTitle>
                <p className="text-zinc-500 text-sm mt-1">Complete analysis with detailed action plan</p>
              </div>
              {user && (
                <div className="flex items-center gap-2">
                  <Badge className={
                    isAdmin ? 'bg-purple-600' :
                    subscription?.subscription_status === 'pending_payment' ? 'bg-yellow-600' :
                    userTier === 'pro' ? 'bg-purple-600' :
                    userTier === 'core' ? 'bg-blue-600' :
                    userTier === 'enterprise' ? 'bg-purple-600' : 'bg-zinc-600'
                  }>
                    {isAdmin ? 'Admin' : 
                     subscription?.subscription_status === 'pending_payment' ? 'Pending Payment' :
                     userTier.charAt(0).toUpperCase() + userTier.slice(1)}
                  </Badge>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Login prompt for non-logged users */}
            {!user && (
              <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                <p className="text-yellow-400 text-sm">
                  <Lock className="w-4 h-4 inline mr-2" />
                  You need to <Link to="/login" className="underline hover:text-yellow-300">log in</Link> and have an active subscription to run a full audit.
                </p>
              </div>
            )}

            {/* PENDING PAYMENT - Enterprise user with quote but not paid */}
            {user && !isAdmin && subscription?.subscription_status === 'pending_payment' && subscription?.enterprise_config && (
              <div className="mb-6 p-4 bg-purple-500/10 border border-purple-500/30 rounded-sm">
                <p className="text-purple-400 text-sm font-semibold mb-2">
                  <Crown className="w-4 h-4 inline mr-2" />
                  Your Enterprise quote is ready!
                </p>
                <p className="text-purple-300 text-sm mb-3">
                  Complete payment to activate your plan with {subscription.enterprise_config.custom_credits} credits/month 
                  and {subscription.enterprise_config.custom_domains} monitored domains.
                </p>
                <Link to="/pricing">
                  <Button className="bg-purple-600 hover:bg-purple-500 text-white">
                    Pay £{subscription.enterprise_config.custom_price}/mo to Activate
                  </Button>
                </Link>
              </div>
            )}

            {/* No credits warning - for paid users (NOT pending_payment) */}
            {user && !isAdmin && userTier !== 'free' && !hasCredits && subscription?.subscription_status !== 'pending_payment' && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                <p className="text-red-400 text-sm">
                  You have no credits remaining. Wait for monthly refresh or upgrade your plan.
                </p>
              </div>
            )}

            {/* Free user - has used their free audit */}
            {user && !isAdmin && freeAuditUsed && subscription?.subscription_status !== 'pending_payment' && (
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                <p className="text-blue-400 text-sm">
                  <Crown className="w-4 h-4 inline mr-2" />
                  You've used your free audit. Upgrade to <Link to="/pricing" className="underline hover:text-blue-300">Core or Pro</Link> for more audits with full action plans.
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Domain Selection - Different UI based on tier and domain status */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-zinc-400 text-xs uppercase tracking-widest font-semibold">Target Domain</Label>
                  {domains.length > 0 && (
                    <span className="text-zinc-500 text-xs">{domains.length}/{maxDomains} domains saved</span>
                  )}
                </div>
                
                {/* Admin: Free text input with autocomplete */}
                {isAdmin && (
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
                    <Input data-testid="target-url-input" value={targetUrl} 
                      onChange={(e) => { setTargetUrl(e.target.value); setShowDomainSuggestions(true); }}
                      onFocus={() => setShowDomainSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowDomainSuggestions(false), 200)}
                      placeholder="example.com"
                      autoComplete="off"
                      className="pl-10 bg-black border-white/20 text-white placeholder-zinc-600 h-12 rounded-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                    {/* Autocomplete dropdown */}
                    {showDomainSuggestions && filteredSuggestions.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                        {filteredSuggestions.map((domain, i) => (
                          <button
                            key={i}
                            type="button"
                            className="w-full px-4 py-2 text-left text-white hover:bg-white/5 text-sm flex items-center gap-2"
                            onClick={() => { setTargetUrl(domain); setShowDomainSuggestions(false); }}
                          >
                            <Globe className="w-3 h-3 text-zinc-500" />
                            {domain}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Core/Pro/Enterprise with no domains yet: Input to set first domain with autocomplete */}
                {!isAdmin && user && (userTier === 'core' || userTier === 'pro' || userTier === 'enterprise') && domains.length === 0 && !showAddDomainInput && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 z-10" />
                      <Input data-testid="target-url-input" value={targetUrl} 
                        onChange={(e) => { setTargetUrl(e.target.value); setShowDomainSuggestions(true); }}
                        onFocus={() => setShowDomainSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowDomainSuggestions(false), 200)}
                        placeholder="Enter your domain (e.g. example.com)"
                        autoComplete="off"
                        className="pl-10 bg-black border-white/20 text-white placeholder-zinc-600 h-12 rounded-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                      {/* Autocomplete dropdown */}
                      {showDomainSuggestions && filteredSuggestions.length > 0 && (
                        <div className="absolute top-full left-0 right-0 mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                          {filteredSuggestions.map((domain, i) => (
                            <button
                              key={i}
                              type="button"
                              className="w-full px-4 py-2 text-left text-white hover:bg-white/5 text-sm flex items-center gap-2"
                              onClick={() => { setTargetUrl(domain); setShowDomainSuggestions(false); }}
                            >
                              <Globe className="w-3 h-3 text-zinc-500" />
                              {domain}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <p className="text-zinc-500 text-xs">
                      This domain will be saved to your account for monitoring.
                    </p>
                  </div>
                )}
                
                {/* Core with domain set: Display with change option */}
                {!isAdmin && userTier === 'core' && domains.length > 0 && !showAddDomainInput && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 p-3 bg-zinc-900/50 border border-white/10 rounded-sm">
                      <Globe className="w-4 h-4 text-blue-400" />
                      <span className="text-white font-medium">{domains[0]?.domain}</span>
                      <Badge className="ml-auto bg-blue-600/20 text-blue-400 text-xs">Primary</Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-zinc-500 text-xs">
                        Core plan monitors 1 domain. <Link to="/pricing" className="text-blue-400 hover:underline">Upgrade to Pro</Link> for up to 3.
                      </p>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm" 
                        className="text-orange-400 hover:text-orange-300 text-xs h-7"
                        onClick={() => setShowAddDomainInput(true)}
                      >
                        Change domain
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Core: Change domain input */}
                {!isAdmin && userTier === 'core' && domains.length > 0 && showAddDomainInput && (
                  <div className="space-y-3 p-4 bg-zinc-900/50 border border-orange-500/30 rounded-sm">
                    <Label className="text-orange-400 text-xs font-semibold">Change Domain</Label>
                    <p className="text-zinc-400 text-xs">Your current domain ({domains[0]?.domain}) will be archived. Historical data will be preserved.</p>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input 
                          data-testid="new-domain-input"
                          value={newDomainInput} 
                          onChange={(e) => setNewDomainInput(e.target.value)}
                          placeholder="newdomain.com"
                          className="pl-10 bg-black border-white/20 text-white placeholder-zinc-600 h-10 rounded-sm focus:border-orange-500"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleChangeDomain())}
                        />
                      </div>
                      <Button 
                        type="button" 
                        onClick={handleChangeDomain} 
                        disabled={addingDomain}
                        className="bg-orange-600 hover:bg-orange-500 h-10 px-4"
                      >
                        {addingDomain ? 'Changing...' : 'Change'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        onClick={() => { setShowAddDomainInput(false); setNewDomainInput(""); }}
                        className="text-zinc-500 hover:text-white h-10 px-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Pro with domains: Dropdown selector + Add button */}
                {!isAdmin && userTier === 'pro' && domains.length > 0 && !showAddDomainInput && (
                  <div className="space-y-2">
                    <Select value={selectedDomainId} onValueChange={handleDomainSelect}>
                      <SelectTrigger data-testid="domain-select" className="bg-black border-white/20 text-white h-12 rounded-sm">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-zinc-500" />
                          <SelectValue placeholder="Select a domain" />
                        </div>
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        {domains.map(d => (
                          <SelectItem key={d.id} value={d.id} className="text-white hover:bg-white/5">
                            {d.domain}
                          </SelectItem>
                        ))}
                        {canAddDomain && (
                          <SelectItem value="__add_new__" className="text-blue-400 hover:bg-white/5">
                            <Plus className="w-4 h-4 inline mr-1" /> Add new domain
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {canAddDomain && (
                      <p className="text-zinc-500 text-xs">
                        Pro plan allows up to {maxDomains} domains. {maxDomains - domains.length} slot(s) remaining.
                      </p>
                    )}
                  </div>
                )}
                
                {/* Add domain input (shown when adding new domain) */}
                {!isAdmin && showAddDomainInput && (
                  <div className="space-y-3 p-4 bg-zinc-900/50 border border-blue-500/30 rounded-sm">
                    <Label className="text-blue-400 text-xs font-semibold">Add New Domain</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input 
                          data-testid="new-domain-input"
                          value={newDomainInput} 
                          onChange={(e) => setNewDomainInput(e.target.value)}
                          placeholder="example.com"
                          className="pl-10 bg-black border-white/20 text-white placeholder-zinc-600 h-10 rounded-sm focus:border-blue-500"
                          onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
                        />
                      </div>
                      <Button 
                        type="button" 
                        onClick={handleAddDomain} 
                        disabled={addingDomain}
                        data-testid="confirm-add-domain-btn"
                        className="bg-blue-600 hover:bg-blue-500 h-10 px-4"
                      >
                        {addingDomain ? 'Adding...' : 'Add'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        onClick={() => { setShowAddDomainInput(false); setNewDomainInput(""); }}
                        className="text-zinc-500 hover:text-white h-10 px-2"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Free user with credits: Input to enter domain (one-time use) */}
                {!isAdmin && user && userTier === 'free' && hasCredits && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                      <Input data-testid="target-url-input" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)}
                        placeholder="Enter your domain (e.g. example.com)"
                        className="pl-10 bg-black border-white/20 text-white placeholder-zinc-600 h-12 rounded-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
                    </div>
                  </div>
                )}
                
                {/* Free user without credits: Upgrade prompt */}
                {!isAdmin && user && userTier === 'free' && !hasCredits && (
                  <div className="p-3 bg-zinc-900/50 border border-white/5 rounded-sm">
                    <p className="text-zinc-500 text-sm">
                      <Link to="/pricing" className="text-blue-400 hover:underline">Upgrade to Core or Pro</Link> to run more audits.
                    </p>
                  </div>
                )}
                
                {/* Not logged in: Show placeholder */}
                {!user && (
                  <div className="p-3 bg-zinc-900/50 border border-white/5 rounded-sm">
                    <p className="text-zinc-500 text-sm">
                      <Link to="/login" className="text-blue-400 hover:underline">Log in</Link> and subscribe to run full audits.
                    </p>
                  </div>
                )}
              </div>

              {/* Competitors section - only for Pro/Admin */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-zinc-400 text-xs uppercase tracking-widest font-semibold">Competitors</Label>
                    {!canAddCompetitors && (
                      <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-xs">
                        <Lock className="w-3 h-3 mr-1" /> Pro only
                      </Badge>
                    )}
                  </div>
                  {canAddCompetitors && competitors.length < 3 && (
                    <Button type="button" variant="ghost" size="sm" onClick={addCompetitor} data-testid="add-competitor-btn"
                      className="text-blue-400 hover:text-blue-300 h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  )}
                </div>
                
                {canAddCompetitors ? (
                  competitors.length > 0 ? (
                    competitors.map((comp, i) => (
                      <div key={i} className="flex gap-2">
                        <Input data-testid={`competitor-url-input-${i}`} value={comp} onChange={(e) => updateCompetitor(i, e.target.value)}
                          placeholder={`competitor${i + 1}.com`}
                          className="bg-black border-white/20 text-white placeholder-zinc-600 h-10 rounded-sm focus:border-blue-500" />
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeCompetitor(i)}
                          data-testid={`remove-competitor-btn-${i}`} className="text-zinc-500 hover:text-red-400 h-10 px-2 shrink-0">
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <p className="text-zinc-600 text-sm">Click "Add" to include competitor analysis (up to 3)</p>
                  )
                ) : (
                  <div className="p-3 bg-zinc-900/50 border border-white/5 rounded-sm">
                    <p className="text-zinc-500 text-sm">
                      Competitor analysis is available on the Pro plan. 
                      <Link to="/pricing" className="text-purple-400 hover:text-purple-300 ml-1">Upgrade now</Link>
                    </p>
                  </div>
                )}
              </div>

              {/* Path Restriction Checkbox */}
              <div className="flex items-center space-x-2 p-3 bg-zinc-900/30 border border-white/10 rounded-sm">
                <Checkbox 
                  id="restrict-path" 
                  checked={restrictPath} 
                  onCheckedChange={setRestrictPath}
                  className="border-white/20 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor="restrict-path"
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-zinc-200"
                  >
                    Restrict scan to path only
                  </Label>
                  <p className="text-xs text-zinc-500">
                    If checked, crawler will NOT go up to parent directories. Useful for scanning only a specific section (e.g. /blog/ or /uk/).
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-widest font-semibold">
                    Region <span className="text-red-400">*</span>
                  </Label>
                  <Select value={region} onValueChange={setRegion}>
                    <SelectTrigger data-testid="region-select" className={`bg-black border-white/20 text-white h-10 rounded-sm ${!region ? 'text-zinc-500' : ''}`}>
                      <SelectValue placeholder="Select target market" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {REGIONS.map(r => <SelectItem key={r.value} value={r.value} className="text-white hover:bg-white/5">{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-zinc-600 text-xs">SEO analysis for this market</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400 text-xs uppercase tracking-widest font-semibold">
                    Language <span className="text-red-400">*</span>
                  </Label>
                  <Select value={language} onValueChange={setLanguage}>
                    <SelectTrigger data-testid="language-select" className={`bg-black border-white/20 text-white h-10 rounded-sm ${!language ? 'text-zinc-500' : ''}`}>
                      <SelectValue placeholder="Select report language" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      {LANGUAGES.map(l => <SelectItem key={l.value} value={l.value} className="text-white hover:bg-white/5">{l.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-zinc-600 text-xs">Report output language</p>
                </div>
              </div>

              {showUpgradeCta ? (
                <Link to="/pricing" className="w-full">
                  <Button type="button" data-testid="upgrade-to-unlock-btn"
                    className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-6 rounded-sm shadow-[0_0_15px_rgba(59,130,246,0.3)] transition-all duration-300 text-base">
                    <span className="flex items-center gap-2"><Crown className="w-5 h-5" /> Upgrade to unlock</span>
                  </Button>
                </Link>
              ) : (
                <Button type="submit" data-testid="start-full-audit-btn" 
                  disabled={loading || !targetUrl.trim() || !region || !language || (!isAdmin && !canRunFullAudit)}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-6 rounded-sm shadow-[0_0_15px_rgba(59,130,246,0.3)] transition-all duration-300 text-base disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Starting Full Audit...</span>
                  ) : !user ? (
                    <span className="flex items-center gap-2"><Lock className="w-5 h-5" /> Log in to continue</span>
                  ) : !canRunFullAudit ? (
                    <span className="flex items-center gap-2"><Crown className="w-5 h-5" /> Upgrade to unlock</span>
                  ) : (
                    <span className="flex items-center gap-2"><Zap className="w-5 h-5" /> Start Full Audit</span>
                  )}
                </Button>
              )}

              {user && !isAdmin && hasCredits && (
                <p className="text-center text-zinc-500 text-sm">
                  This will use 1 credit. You have {subscription?.credits_remaining || 0} credits remaining.
                  <br />
                  <span className="text-zinc-600">Full Depth scan (unlimited pages)</span>
                </p>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>

    {/* Large site warning dialog */}
    <AlertDialog open={!!largeSiteWarning} onOpenChange={(open) => !open && setLargeSiteWarning(null)}>
      <AlertDialogContent className="bg-[#121212] border-white/10">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
            Large Website Detected
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-400 space-y-2">
            <p>
              The sitemap contains <span className="text-white font-semibold">{largeSiteWarning?.url_count?.toLocaleString()}</span> URLs.
            </p>
            <p>
              Scanning this many pages may take a long time and the application may struggle with very large datasets. 
              Are you sure you want to proceed?
            </p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={() => { setLargeSiteWarning(null); runAudit(); }}
            className="bg-amber-600 hover:bg-amber-500 text-white"
          >
            Proceed Anyway
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    </AppLayout>
  );
}
